import type { JSX } from 'react'
import { useAgents } from '../../../hooks/useAgents.js'
import { useContactCenterQueues } from '../../../hooks/useContactCenterQueues.js'
import { useSkills } from '../../../hooks/useSkills.js'
import Select from '../../ui/Select.js'

/**
 * Discriminated union of routing actions emitted by a rule match.
 * Mirrors the action shapes consumed by the RoutingEngine
 * (apps/api/.../conversations RoutingEngine).
 */
export type RoutingAction =
  | { type: 'assign_agent'; agentId: string }
  | { type: 'assign_queue'; queueId: string }
  | { type: 'assign_by_skills'; skillIds: string[]; queueId?: string }

export interface RoutingActionPickerProps {
  value: RoutingAction[]
  onChange: (next: RoutingAction[]) => void
}

const EMPTY_SKILL_IDS: string[] = []

function blankAction(): RoutingAction {
  return { type: 'assign_agent', agentId: '' }
}

function actionForType(type: RoutingAction['type']): RoutingAction {
  switch (type) {
    case 'assign_agent':
      return { type: 'assign_agent', agentId: '' }
    case 'assign_queue':
      return { type: 'assign_queue', queueId: '' }
    case 'assign_by_skills':
      return { type: 'assign_by_skills', skillIds: [] }
  }
}

export default function RoutingActionPicker({
  value,
  onChange,
}: RoutingActionPickerProps): JSX.Element {
  const { agents } = useAgents()
  const { items: queues } = useContactCenterQueues()
  const { skills } = useSkills()

  function updateAt(i: number, next: RoutingAction): void {
    onChange(value.map((a, idx) => (idx === i ? next : a)))
  }

  function removeAt(i: number): void {
    onChange(value.filter((_, idx) => idx !== i))
  }

  function addAction(): void {
    onChange([...value, blankAction()])
  }

  return (
    <div className="rap flex flex-col gap-2">
      {value.length === 0 && (
        <p className="text-sm text-gray-500">No actions configured.</p>
      )}
      {value.map((action, i) => (
        <div
          key={i}
          role="group"
          aria-label={`Routing action ${i + 1}`}
          className="rap-row flex items-center gap-2 rounded border border-gray-300 p-2"
        >
                    <Select
            ariaLabel={`Action type ${i + 1}`}
            size="sm"
            value={action.type}
            onChange={(v) => updateAt(i, actionForType(v as RoutingAction['type']))}
          >
            <option value="assign_agent">Assign to agent</option>
            <option value="assign_queue">Assign to queue</option>
            <option value="assign_by_skills">Assign by skills</option>
                    </Select>

          {action.type === 'assign_agent' && (
                        <Select
              ariaLabel={`Agent ${i + 1}`}
              size="sm"
              value={action.agentId}
              onChange={(v) => updateAt(i, { type: 'assign_agent', agentId: v })}
            >
              <option value="">— select agent —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.extension}
                </option>
              ))}
                        </Select>
          )}

          {action.type === 'assign_queue' && (
                        <Select
              ariaLabel={`Queue ${i + 1}`}
              size="sm"
              value={action.queueId}
              onChange={(v) => updateAt(i, { type: 'assign_queue', queueId: v })}
            >
              <option value="">— select queue —</option>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
                        </Select>
          )}

          {action.type === 'assign_by_skills' && (
            <select
              aria-label={`Skills ${i + 1}`}
              multiple
              value={action.skillIds ?? EMPTY_SKILL_IDS}
              onChange={(e) => {
                const opts = Array.from(e.target.selectedOptions).map(
                  (o) => o.value,
                )
                const next: RoutingAction = {
                  type: 'assign_by_skills',
                  skillIds: opts,
                }
                if (action.queueId !== undefined) {
                  next.queueId = action.queueId
                }
                updateAt(i, next)
              }}
              className="rounded border border-gray-300 px-1 py-0.5 text-xs"
            >
              {skills.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            aria-label={`Remove action ${i + 1}`}
            className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs"
            onClick={() => removeAt(i)}
          >
            Remove
          </button>
        </div>
      ))}

      <div>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={addAction}
        >
          + Add action
        </button>
      </div>
    </div>
  )
}
